import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FirstPageRounded,
  LastPageRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
} from "@mui/icons-material";
import styles from "./ReviewList.module.css";
import {
  useLoaderContext,
  useToastContext,
} from "../../../Context/ToastContext";
import { Helmet } from "react-helmet";
import StarIcon from "@mui/icons-material/Star";
import { apiGet } from "../../../utils/ApiManager";
import Dropdown1 from "./../Dropdown/Dropdown1";
import { Grid } from "@mui/system";

const getReviews = process.env.REACT_APP_RESTAURANT_RATINGS;
const fetchRestaurants = process.env.REACT_APP_FETCH_RESTAURANTS_VIEW_BOOKING;

const ReviewList = () => {
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const { showToast } = useToastContext();
  const { setLoading } = useLoaderContext();
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const startColors = ["#E74C3C", "#E67E22", "#F1C40F", "#2ECC71", "#27AE60	"];

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const getRestaurants = async () => {
    try {
      const response = await apiGet(fetchRestaurants);
      if (response.success) {
        const restaurantList = response?.data?.restaurantList;
        setRestaurantOptions(restaurantList);
      } else {
        showToast(
          response?.message || "Failed to fetch dropdown !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to fetch dinings !",
        "error"
      );
    }
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      let query = {
        pageSize: rowsPerPage,
        pageNo: page,
        searchText: searchQuery,
      };
      if (selectedRestaurant) {
        query.restaurantId = selectedRestaurant;
      }
      if (sortOrder) {
        query.sortOrder = sortOrder;
      }
      query = new URLSearchParams(query).toString();
      const response = await apiGet(`${getReviews}?${query}`);
      if (response.success) {
        setReviews(response?.data || []);
        setTotalCount(response?.totalCount || 0);
      } else {
        showToast(response?.message || "Error fetching reviews list.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message || error?.message || "Error fetching reviews.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchReviews();
  }, [page, rowsPerPage, selectedRestaurant, sortOrder]);

  useEffect(() => {
    getRestaurants();
  }, []);

  const filteredReviews = reviews
    .filter((review) =>
      selectedRestaurant
        ? review.restaurantId?.restaurantId === selectedRestaurant.restaurantId
        : true
    )
    .sort((a, b) => {
      const ra = a.rating || 0;
      const rb = b.rating || 0;
      return sortOrder === "asc" ? ra - rb : rb - ra;
    });

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Reviews | DiNEST</title>
        <meta name="description" content="View diners on DiNEST" />
      </Helmet>
      <Box className={styles.restaurantListContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Diner Reviews
        </Typography>

        {(reviews?.length > 0 || selectedRestaurant) ? (
          <>
            <Box className={styles.searchBarContainer}>
              {" "}
              <Grid sx={{ width: 250 }} className={styles.gridInput}>
                <Dropdown1
                  label="Filter by Restaurant"
                  name="restaurant"
                  value={selectedRestaurant}
                  options={restaurantOptions}
                  //   getOptionLabel={(opt) => opt.restaurantName}
                  onChange={(e) => setSelectedRestaurant(e.target.value)}
                />
              </Grid>
              <Grid sx={{ width: 250 }} className={styles.gridInput}>
                <Dropdown1
                  label="Sort by Rating"
                  name="sortRating"
                  options={[
                    { value: "desc", label: "Highest → Lowest" },
                    { value: "asc", label: "Lowest → Highest" },
                  ]}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </Grid>
            </Box>

            <TableContainer className={styles.tableContainer} component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>Sr. No</TableCell>
                    <TableCell className={styles.tableHead}>
                      Booking ID
                    </TableCell>
                    <TableCell className={styles.tableHead}>Diner</TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Name
                    </TableCell>
                    <TableCell className={styles.tableHead}>Rating</TableCell>
                    <TableCell className={styles.tableHead}>
                      Booking Date
                    </TableCell>
                    <TableCell className={styles.tableHead}>Comment</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reviews.map((review, index) => (
                    <TableRow
                      key={index}
                      style={{ opacity: review?.isDeleted ? 0.4 : 1 }}
                      className={styles.tableDataContainer}
                    >
                      <TableCell className={styles.tableData}>
                        {rowsPerPage * (page - 1) + index + 1}
                      </TableCell>
                      <TableCell className={styles.tableData}>
                        {review?.bookingId?.bookingId || "N/A"}
                      </TableCell>

                      <TableCell
                        className={styles.tableData}
                        sx={{ maxWidth: 150 }}
                      >
                        <Tooltip
                          title={
                            review?.userId?.firstName
                              ? `${review?.userId?.firstName} ${review?.userId?.lastName || ""
                              }`
                              : "N/A"
                          }
                          arrow
                        >
                          <Typography
                            style={{
                              display: "inline-block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 70,
                            }}
                          >
                            {`${review?.userId?.firstName || "N/A"} ${review?.userId?.lastName || ""
                              }`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={styles.tableData}>
                        <Tooltip
                          title={review?.restaurantId?.restaurantName || "N/A"}
                          arrow
                        >
                          <Typography
                            style={{
                              display: "inline-block",
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {review?.restaurantId?.restaurantName || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={styles.tableData}>
                        {review?.rating || "N/A"}
                        <StarIcon
                          sx={{ color: startColors[review?.rating - 1] }}
                        />
                      </TableCell>
                      <TableCell className={styles.tableData}>
                        {review?.bookingId?.bookingDate
                          ? `${new Date(
                            review.bookingId?.bookingDate
                          ).toLocaleDateString("en-GB")} ${review?.bookingId?.startTime
                          }`
                          : "N/A"}
                      </TableCell>
                      <TableCell className={styles.tableData}>
                        <Tooltip title={review?.comment || "N/A"} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "inline-block",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            {review?.comment || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div className={styles.paginationContainer}>
              <TablePagination
                className={styles.pagination}
                count={totalCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 15]}
                sx={{
                  "& .MuiTablePagination-actions button": {
                    backgroundColor: "transparent",
                    color: "white",
                  },
                  "& .MuiSelect-icon": {
                    fill: "white",
                  },
                }}
                slotProps={{
                  actions: {
                    showFirstButton: true,
                    showLastButton: true,
                    slots: {
                      firstPageIcon: FirstPageRounded,
                      lastPageIcon: LastPageRounded,
                      nextPageIcon: ChevronRightRounded,
                      backPageIcon: ChevronLeftRounded,
                    },
                  },
                }}
              />
            </div>
          </>
        ) : (
          <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
            No reviews yet !
          </h2>
        )}
      </Box>
    </Box>
  );
};

export default ReviewList;
